import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputBase,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { AttachFile, InsertDriveFileOutlined, Send } from "@mui/icons-material";
import User from "../../models/User";
import { CHAT_MAX_ATTACHMENT_BYTES, ChatOutgoingAttachment } from "../../models/Chat";
import ChatAvatar from "./ChatAvatar";
import {
  formatFileSize,
  mentionsInBody,
  mentionToken,
  readFileAsBase64,
  userDisplayName,
} from "./chatUtils";

export interface ComposerSubmit {
  body: string;
  mentionUserIds: number[];
  attachments: ChatOutgoingAttachment[];
}

interface ComposerProps {
  placeholder: string;
  mentionCandidates: User[];
  usersById: Map<number, User>;
  onSubmit: (input: ComposerSubmit) => Promise<void>;
  autoFocus?: boolean;
  /** Edit mode: prefilled text, no attachments, shows Cancel/Save. */
  initialBody?: string;
  initialMentionIds?: number[];
  onCancel?: () => void;
  dense?: boolean;
}

const MAX_SUGGESTIONS = 8;

function activeMentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const match = before.match(/(^|\s)@([^\s@]{0,30})$/);
  if (!match) return null;
  return { start: caret - match[2].length - 1, query: match[2].toLowerCase() };
}

const Composer: React.FC<ComposerProps> = ({
  placeholder,
  mentionCandidates,
  usersById,
  onSubmit,
  autoFocus,
  initialBody,
  initialMentionIds,
  onCancel,
  dense,
}) => {
  const editing = initialBody !== undefined;
  const [body, setBody] = useState(initialBody ?? "");
  const [files, setFiles] = useState<File[]>([]);
  const [pickedMentions, setPickedMentions] = useState<number[]>(initialMentionIds ?? []);
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [highlight, setHighlight] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const suggestions = useMemo(() => {
    if (!mention) return [];
    const q = mention.query;
    return mentionCandidates
      .filter((u) => {
        if (!q) return true;
        const name = userDisplayName(u).toLowerCase();
        return name.split(/\s+/).some((part) => part.startsWith(q)) || name.startsWith(q);
      })
      .slice(0, MAX_SUGGESTIONS);
  }, [mention, mentionCandidates]);

  const updateMention = (text: string, caret: number) => {
    const next = activeMentionQuery(text, caret);
    setMention(next);
    setHighlight(0);
  };

  const insertMention = (user: User) => {
    if (!mention) return;
    const token = `${mentionToken(user)} `;
    const caret = inputRef.current?.selectionStart ?? body.length;
    const next = body.slice(0, mention.start) + token + body.slice(caret);
    setBody(next);
    setPickedMentions((prev) => (prev.includes(user.id) ? prev : [...prev, user.id]));
    setMention(null);
    const nextCaret = mention.start + token.length;
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const addFiles = useCallback((incoming: File[]) => {
    const tooBig = incoming.filter((f) => f.size > CHAT_MAX_ATTACHMENT_BYTES);
    if (tooBig.length > 0) {
      setError(`${tooBig.map((f) => f.name).join(", ")} is larger than 15MB`);
    }
    const accepted = incoming.filter((f) => f.size <= CHAT_MAX_ATTACHMENT_BYTES && f.size > 0);
    if (accepted.length > 0) setFiles((prev) => [...prev, ...accepted]);
  }, []);

  const canSend = !sending && (body.trim().length > 0 || files.length > 0);

  const submit = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      const attachments = await Promise.all(
        files.map(async (file) => ({
          fileName: file.name,
          contentType: file.type || "application/octet-stream",
          contentBase64: await readFileAsBase64(file),
        }))
      );
      await onSubmit({
        body,
        mentionUserIds: mentionsInBody(body, pickedMentions, usersById),
        attachments,
      });
      if (!editing) {
        setBody("");
        setFiles([]);
        setPickedMentions([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send");
    } finally {
      setSending(false);
      window.requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (mention && suggestions.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlight((h) => (h + 1) % suggestions.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        insertMention(suggestions[highlight] ?? suggestions[0]);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setMention(null);
        return;
      }
    }
    if (event.key === "Escape" && onCancel) {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      {mention && suggestions.length > 0 && (
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            bottom: "100%",
            left: 0,
            mb: 0.5,
            minWidth: 260,
            maxHeight: 280,
            overflowY: "auto",
            zIndex: 10,
            py: 0.5,
          }}
        >
          {suggestions.map((user, index) => (
            <Stack
              key={user.id}
              direction="row"
              alignItems="center"
              gap={1}
              onMouseDown={(event) => {
                event.preventDefault();
                insertMention(user);
              }}
              onMouseEnter={() => setHighlight(index)}
              sx={{
                px: 1.5,
                py: 0.75,
                cursor: "pointer",
                bgcolor: index === highlight ? "action.selected" : undefined,
              }}
            >
              <ChatAvatar user={user} size={24} />
              <Typography variant="body2">{userDisplayName(user)}</Typography>
            </Stack>
          ))}
        </Paper>
      )}

      <Paper
        variant="outlined"
        onDragOver={(event) => {
          if (editing) return;
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          if (editing) return;
          event.preventDefault();
          setDragOver(false);
          addFiles(Array.from(event.dataTransfer.files ?? []));
        }}
        sx={{
          borderRadius: 1,
          borderColor: dragOver ? "primary.main" : undefined,
          borderBottomWidth: 2,
          borderBottomColor: "primary.main",
          bgcolor: "background.paper",
        }}
      >
        {files.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ px: 1.5, pt: 1 }}>
            {files.map((file, index) => (
              <Chip
                key={`${file.name}-${index}`}
                size="small"
                icon={<InsertDriveFileOutlined />}
                label={`${file.name} (${formatFileSize(file.size)})`}
                onDelete={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
              />
            ))}
          </Stack>
        )}
        <InputBase
          inputRef={inputRef}
          value={body}
          onChange={(event) => {
            setBody(event.target.value);
            updateMention(event.target.value, event.target.selectionStart ?? event.target.value.length);
          }}
          onKeyDown={onKeyDown}
          onClick={(event) => {
            const target = event.target as HTMLTextAreaElement;
            updateMention(target.value, target.selectionStart ?? target.value.length);
          }}
          onBlur={() => setMention(null)}
          onPaste={(event) => {
            if (editing) return;
            const pasted = Array.from(event.clipboardData.files ?? []);
            if (pasted.length > 0) {
              event.preventDefault();
              addFiles(pasted);
            }
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          multiline
          minRows={dense ? 1 : 2}
          maxRows={10}
          fullWidth
          sx={{ px: 1.5, pt: 1, pb: 0.5, fontSize: "0.9rem" }}
        />
        <Stack direction="row" alignItems="center" gap={0.5} sx={{ px: 0.5, pb: 0.5 }}>
          {!editing && (
            <>
              <Tooltip title="Attach files">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Attach files"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <AttachFile fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(event) => {
                  addFiles(Array.from(event.target.files ?? []));
                  event.target.value = "";
                }}
              />
            </>
          )}
          {(editing || !dense) && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ ml: 0.5, minWidth: 0 }}>
              {editing ? "Esc to cancel" : "Shift+Enter for a new line"}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {editing ? (
            <>
              <Button size="small" onClick={onCancel} disabled={sending}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={() => void submit()} disabled={!canSend}>
                Save
              </Button>
            </>
          ) : (
            <Tooltip title="Send">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="Send"
                  disabled={!canSend}
                  onClick={() => void submit()}
                >
                  {sending ? <CircularProgress size={18} /> : <Send fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Paper>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 0.5, py: 0 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default Composer;
